'use client';

import { DuckDBEngine } from '@/lib/engine';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

interface DuckDBContextType {
  engine: DuckDBEngine | null;
  isLoading: boolean;
  error: Error | null;
}

const DuckDBContext = createContext<DuckDBContextType | undefined>(undefined);

interface DuckDBProviderProps {
  children: ReactNode;
}

export function DuckDBProvider({ children }: DuckDBProviderProps) {
  const [engine, setEngine] = useState<DuckDBEngine | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function initEngine() {
      try {
        const engine = await DuckDBEngine.create();
        setEngine(engine);
        setIsLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to initialize DuckDB engine'));
        setIsLoading(false);
      }
    }

    void initEngine();

    return () => {
      // Cleanup when the provider is unmounted
      if (engine) {
        void engine.dispose();
      }
    };
  }, []);

  return (
    <DuckDBContext.Provider value={{ engine, isLoading, error }}>
      {children}
    </DuckDBContext.Provider>
  );
}

export function useEngine() {
  const context = useContext(DuckDBContext);
  if (context === undefined) {
    throw new Error('useEngine must be used within a DuckDBProvider');
  }
  return context;
}

// Logger implementation
class ConsoleLogger {
  debug(...args: unknown[]) {
    console.debug(...args);
  }
  
  info(...args: unknown[]) {
    console.info(...args);
  }
  
  warn(...args: unknown[]) {
    console.warn(...args);
  }
  
  error(...args: unknown[]) {
    console.error(...args);
  }
} 